/**
 * 課題管理の台帳の読み書き（oRPC `issueMgmt` / `issueMgmtWrite`）。
 *
 * 書き込みが成功したら課題管理の読み取りをまとめて読み直す（一覧・詳細・変更履歴・設定の食い違いを残さない）。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題管理の一覧・詳細・変更履歴・担当の候補・テーマ・設定を読み、課題の作成・編集・判定・統合・声を入れる・対応した・再発した・設定・元に戻すを書く
 * @graph-connects rpcClient [calls] issueMgmt・issueMgmtWrite の手続き
 */
import {
  keepPreviousData,
  type QueryKey,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { rpcClient } from '../lib/orpc.js';
import { queryKeys } from '../lib/query-keys.js';

/**
 * 一覧の条件。
 * @graph-connects none
 */
export interface IssueListFilter {
  query: string;
  themeId: string | null;
  includeHidden: boolean;
  /** 課題に紐づいていない声を何件まで出すか（検索したときだけ使う） */
  unlinkedLimit: number;
}

/**
 * 課題の一覧と検索。検索語を打つ間は前の結果を出したままにする（表がちらつかないように）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題の一覧と、課題と声を1つの欄で探す検索の結果を取得する
 * @graph-connects rpcClient [calls] issueMgmt.list
 * @graph-connects queryKeys [calls] Query key factory
 */
export function useIssueList(filter: IssueListFilter, enabled = true) {
  return useQuery({
    queryKey: queryKeys.issueMgmt.list(
      filter.query,
      filter.themeId,
      filter.includeHidden,
      filter.unlinkedLimit,
    ),
    queryFn: () =>
      rpcClient.issueMgmt.list({
        ...(filter.query === ''
          ? {}
          : { query: filter.query, unlinkedLimit: filter.unlinkedLimit }),
        ...(filter.themeId === null ? {} : { themeId: filter.themeId }),
        ...(filter.includeHidden ? { includeHidden: true } : {}),
      }),
    placeholderData: keepPreviousData,
    enabled,
  });
}

/**
 * 課題1件の詳細。enabled が false なら読まない（統合先を選ぶまでなど）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題1件の詳細（見出し・中身3項目・声・案件・テーマ・統合）を取得する
 * @graph-connects rpcClient [calls] issueMgmt.detail
 * @graph-connects queryKeys [calls] Query key factory
 */
export function useIssueDetail(issueId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.issueMgmt.detail(issueId),
    queryFn: () => rpcClient.issueMgmt.detail({ issueId }),
    enabled,
  });
}

/**
 * 変更履歴を一度に読む行数（API の上限 100 より小さく、続きは offset で読む）。
 * @graph-connects none
 */
export const ISSUE_HISTORY_PAGE_SIZE = 20;

/**
 * 課題1件の変更履歴（新しい順）。ISSUE_HISTORY_PAGE_SIZE 行ずつ offset を進めて続きを読む。
 * 届いた行が1回分に満たなければ、それで終わり。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題1件の変更履歴を新しい順に、決まった行数ずつ続きを読みながら取得する
 * @graph-connects rpcClient [calls] issueMgmt.history
 * @graph-connects queryKeys [calls] Query key factory
 */
export function useIssueHistory(issueId: string) {
  return useInfiniteQuery({
    queryKey: queryKeys.issueMgmt.history(issueId),
    queryFn: ({ pageParam }) =>
      rpcClient.issueMgmt.history({ issueId, limit: ISSUE_HISTORY_PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last, pages) =>
      last.entries.length < ISSUE_HISTORY_PAGE_SIZE
        ? undefined
        : pages.length * ISSUE_HISTORY_PAGE_SIZE,
  });
}

/**
 * 領域の選択肢（台帳で使われている領域）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題の領域の選択肢を取得する
 * @graph-connects rpcClient [calls] issueMgmt.domains
 * @graph-connects queryKeys [calls] Query key factory
 */
export function useIssueDomains() {
  return useQuery({
    queryKey: queryKeys.issueMgmt.domains,
    queryFn: () => rpcClient.issueMgmt.domains(),
    staleTime: 5 * 60_000,
  });
}

/**
 * 担当の候補（課題管理の編集キーを持つ人）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 担当・当番・判定者の候補（課題管理の編集キーを持つ人）を取得する
 * @graph-connects rpcClient [calls] issueMgmt.members
 * @graph-connects queryKeys [calls] Query key factory
 */
export function useIssueMembers() {
  return useQuery({
    queryKey: queryKeys.issueMgmt.members,
    queryFn: () => rpcClient.issueMgmt.members(),
    staleTime: 5 * 60_000,
  });
}

/**
 * テーマの一覧。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題に付けるテーマの一覧を取得する
 * @graph-connects rpcClient [calls] issueMgmt.themes
 * @graph-connects queryKeys [calls] Query key factory
 */
export function useIssueThemes() {
  return useQuery({
    queryKey: queryKeys.issueMgmt.themes,
    queryFn: () => rpcClient.issueMgmt.themes(),
  });
}

/**
 * 課題管理の設定。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題管理の設定（当番・判定者・週次の曜日・AIのおすすめ）を取得する
 * @graph-connects rpcClient [calls] issueMgmt.settings
 * @graph-connects queryKeys [calls] Query key factory
 */
export function useIssueSettings() {
  return useQuery({
    queryKey: queryKeys.issueMgmt.settings,
    queryFn: () => rpcClient.issueMgmt.settings(),
  });
}

/**
 * 書き込みの手続き。
 * @graph-connects none
 */
type WriteClient = typeof rpcClient.issueMgmtWrite;

/**
 * 書き込みの手続き名。
 * @graph-connects none
 */
export type IssueMgmtWriteName = keyof WriteClient;

/**
 * 書き込みの手続きの入力。
 * @graph-connects none
 */
type WriteInput<K extends IssueMgmtWriteName> = Parameters<WriteClient[K]>[0];

/**
 * 書き込みの手続きの結果。
 * @graph-connects none
 */
type WriteOutput<K extends IssueMgmtWriteName> = Awaited<ReturnType<WriteClient[K]>>;

/**
 * 台帳を変える書き込みのあとに読み直す読み取り（一覧・詳細・変更履歴・領域・案件欄・やることのカード）。
 * @graph-connects queryKeys [references] 読み直す読み取りのキー
 */
export function ledgerStaleKeys(): QueryKey[] {
  return [
    queryKeys.issueMgmt.lists,
    queryKeys.issueMgmt.details,
    queryKeys.issueMgmt.histories,
    queryKeys.issueMgmt.domains,
    queryKeys.issueMgmt.casesAll,
    queryKeys.issueMgmt.todoCards,
  ];
}

/**
 * 書き込みのあとに読み直す読み取り。課題の中身を変える書き込みは台帳の読み取り（一覧・詳細・変更履歴・領域・
 * 案件欄・やることのカード）、テーマは加えてテーマの一覧、今すぐ同期は加えて同期の状況、設定は設定とやること
 * （当番・判定者でカードの受け持ちが変わる）、今すぐ通知は通知の状況だけ。週次の記録は何も読み直さない
 * （判定のカードを開くたびに読み直すと、カードが並び替わって開いたカードが動くため）。
 * 担当の候補は書き込みでは変わらないので読み直さない。
 * @graph-connects queryKeys [references] 読み直す読み取りのキー
 * @graph-connects ledgerStaleKeys [calls] 台帳の読み取り
 */
export function staleKeysAfter(name: IssueMgmtWriteName): QueryKey[] {
  switch (name) {
    case 'updateSettings':
      return [queryKeys.issueMgmt.settings, queryKeys.issueMgmt.todoCards];
    case 'recordReviewOpen':
      return [];
    case 'notifyStaleNow':
      return [queryKeys.issueMgmt.staleNotify];
    case 'syncBacklog':
      return [...ledgerStaleKeys(), queryKeys.issueMgmt.backlogSync];
    case 'createTheme':
    case 'updateTheme':
      return [...ledgerStaleKeys(), queryKeys.issueMgmt.themes];
    default:
      return ledgerStaleKeys();
  }
}

/**
 * 課題管理の書き込み1つ。成功したら関係する読み取りの読み直しを投げる（終わるのは待たない）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題管理の書き込みを1つ行い、成功したら関係する読み取り（一覧・詳細・変更履歴・テーマ・設定）の読み直しを待たずに投げる
 * @graph-connects rpcClient [calls] issueMgmtWrite の手続き
 * @graph-connects staleKeysAfter [calls] 読み直す読み取り
 */
export function useIssueMgmtWrite<K extends IssueMgmtWriteName>(name: K) {
  const queryClient = useQueryClient();
  const procedure = rpcClient.issueMgmtWrite[name] as (
    input: WriteInput<K>,
  ) => Promise<WriteOutput<K>>;
  return useMutation<WriteOutput<K>, Error, WriteInput<K>>({
    mutationFn: (input) => procedure(input),
    onSuccess: () => {
      for (const queryKey of staleKeysAfter(name)) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}
